import React, { useState, useEffect } from 'react';
import Layout from '../../components/ui/Layout';
import NavigationPanel from './components/NavigationPanel';
import CategoriesSection from './components/CategoriesSection';
import CitiesSection from './components/CitiesSection';
import CategoryCityMapping from './components/CategoryCityMapping';
import { Toaster } from 'react-hot-toast';

const CatalogueGeoManagement = () => {
  const [activeSection, setActiveSection] = useState('categories');

  // Mock data for sections
  const [categories, setCategories] = useState([
    {
      id: 1,
      name: 'Electronics',
      description: 'Electronic devices and accessories',
      status: 'active',
      subcategories: [
        { id: 11, name: 'Smartphones', status: 'active', parentId: 1 },
        { id: 12, name: 'Laptops', status: 'active', parentId: 1 },
        { id: 13, name: 'Tablets', status: 'inactive', parentId: 1 }
      ],
      createdAt: '2024-01-15',
      updatedAt: '2024-10-20'
    },
    {
      id: 2,
      name: 'Fashion',
      description: 'Clothing and fashion accessories',
      status: 'active',
      subcategories: [
        { id: 21, name: 'Men\'s Clothing', status: 'active', parentId: 2 },
        { id: 22, name: 'Women\'s Clothing', status: 'active', parentId: 2 },
        { id: 23, name: 'Accessories', status: 'active', parentId: 2 }
      ],
      createdAt: '2024-02-10',
      updatedAt: '2024-10-18'
    },
    {
      id: 3,
      name: 'Home & Garden',
      description: 'Home improvement and garden supplies',
      status: 'inactive',
      subcategories: [
        { id: 31, name: 'Furniture', status: 'active', parentId: 3 },
        { id: 32, name: 'Garden Tools', status: 'active', parentId: 3 }
      ],
      createdAt: '2024-03-05',
      updatedAt: '2024-10-15'
    }
  ]);

  const [cities, setCities] = useState([
    {
      id: 1,
      name: 'New York',
      country: 'United States',
      state: 'New York',
      status: 'active',
      areas: [
        { id: 11, name: 'Manhattan', pinCodes: ['10001', '10002', '10003'], coordinates: { lat: 40.7831, lng: -73.9712 } },
        { id: 12, name: 'Brooklyn', pinCodes: ['11201', '11202', '11203'], coordinates: { lat: 40.6782, lng: -73.9442 } },
        { id: 13, name: 'Queens', pinCodes: ['11101', '11102', '11103'], coordinates: { lat: 40.7282, lng: -73.7949 } }
      ],
      deliveryRadius: 25,
      coordinates: { lat: 40.7128, lng: -74.0060 },
      createdAt: '2024-01-20',
      updatedAt: '2024-10-22'
    },
    {
      id: 2,
      name: 'Los Angeles',
      country: 'United States',
      state: 'California',
      status: 'active',
      areas: [
        { id: 21, name: 'Hollywood', pinCodes: ['90028', '90038', '90068'], coordinates: { lat: 34.0928, lng: -118.3287 } },
        { id: 22, name: 'Beverly Hills', pinCodes: ['90210', '90211', '90212'], coordinates: { lat: 34.0736, lng: -118.4004 } },
        { id: 23, name: 'Santa Monica', pinCodes: ['90401', '90402', '90403'], coordinates: { lat: 34.0195, lng: -118.4912 } }
      ],
      deliveryRadius: 30,
      coordinates: { lat: 34.0522, lng: -118.2437 },
      createdAt: '2024-02-15',
      updatedAt: '2024-10-20'
    },
    {
      id: 3,
      name: 'London',
      country: 'United Kingdom',
      state: 'England',
      status: 'inactive',
      areas: [
        { id: 31, name: 'Westminster', pinCodes: ['SW1A', 'SW1E', 'SW1H'], coordinates: { lat: 51.4994, lng: -0.1319 } },
        { id: 32, name: 'Camden', pinCodes: ['NW1', 'NW3', 'NW8'], coordinates: { lat: 51.5290, lng: -0.1255 } }
      ],
      deliveryRadius: 20,
      coordinates: { lat: 51.5074, lng: -0.1278 },
      createdAt: '2024-03-10',
      updatedAt: '2024-10-18'
    }
  ]);

  const [categoryMappings, setCategoryMappings] = useState([
    { id: 1, categoryId: 1, cityId: 1, status: 'active', createdAt: '2024-05-01' },
    { id: 2, categoryId: 1, cityId: 2, status: 'active', createdAt: '2024-05-02' },
    { id: 3, categoryId: 2, cityId: 1, status: 'active', createdAt: '2024-05-03' },
    { id: 4, categoryId: 2, cityId: 3, status: 'inactive', createdAt: '2024-05-04' },
    { id: 5, categoryId: 3, cityId: 2, status: 'active', createdAt: '2024-05-05' }
  ]);

  const renderActiveSection = () => {
    switch (activeSection) {
      case 'categories':
        return (
          <CategoriesSection
            categories={categories}
            setCategories={setCategories}
            globalSearch=""
            refreshTrigger={0}
          />
        );
      case 'cities':
        return (
          <CitiesSection
            cities={cities}
            setCities={setCities}
            globalSearch=""
            refreshTrigger={0}
          />
        );
      case 'mapping':
        return (
          <CategoryCityMapping
            categories={categories}
            cities={cities}
            mappings={categoryMappings}
            setMappings={setCategoryMappings}
          />
        );
      default:
        return (
          <CategoriesSection
            categories={categories}
            setCategories={setCategories}
            globalSearch=""
            refreshTrigger={0}
          />
        );
    }
  };

  return (
    <Layout>
      <div className="min-h-screen bg-background">
        {/* Header */}
        <div className="bg-card border-b border-border px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-foreground">Catalogue & Geo Management</h1>
              <p className="text-sm text-muted-foreground mt-1">
                Manage marketplace categories, geographic coverage, and their relationships
              </p>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex h-[calc(100vh-120px)]">
          {/* Navigation Panel */}
          <NavigationPanel
            activeSection={activeSection}
            setActiveSection={setActiveSection}
            categories={categories}
            cities={cities}
            mappings={categoryMappings}
          />

          {/* Content Area */}
          <div className="flex-1 overflow-hidden">
            {renderActiveSection()}
          </div>
        </div>

        {/* Toast Notifications */}
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: 'hsl(var(--card))',
              color: 'hsl(var(--foreground))',
              border: '1px solid hsl(var(--border))',
            },
            success: {
              iconTheme: {
                primary: 'hsl(var(--primary))',
                secondary: 'hsl(var(--primary-foreground))',
              },
            },
            error: {
              iconTheme: {
                primary: 'hsl(var(--destructive))',
                secondary: 'hsl(var(--destructive-foreground))',
              },
            },
          }}
        />
      </div>
    </Layout>
  );
};

export default CatalogueGeoManagement;